import matter from 'gray-matter';

export interface ServiceData {
  order: number;
  title: string;
  description: string;
  icon: string;
  iconAlternates?: string[];
  includes: (string | { text: string; hasTooltip?: boolean; tooltipContent?: string })[];
  content?: string;
  slug: string;
}

export interface TestimonialData {
  order: number;
  name: string;
  role: string;
  company?: string;
  rating: number;
  featured?: boolean;
  date?: string;
  content: string;
  slug: string;
}


// Load all services from markdown files
export async function loadServices(): Promise<ServiceData[]> {
  const services: ServiceData[] = [];
  
  try {
    // Import all service markdown files
    const modules = import.meta.glob('/src/content/services/*.md', { query: '?raw', import: 'default' });
    
    console.log('Loading services, modules found:', Object.keys(modules).length);
    
    for (const path in modules) {
      const content = await modules[path]();
      console.log('Loading service from:', path, 'Content type:', typeof content);
      if (typeof content === 'string') {
        const { data, content: markdownContent } = matter(content);
        
        // Extract slug from filename
        const filename = path.split('/').pop() || '';
        const slug = filename.replace('.md', '');
        
        services.push({
          order: data.order || 0,
          title: data.title || '',
          description: data.description || '',
          icon: data.icon || 'HelpCircle',
          iconAlternates: data.iconAlternates || [],
          includes: data.includes || [],
          content: markdownContent,
          slug
        });
      }
    }
    
    // Sort by order
    services.sort((a, b) => a.order - b.order);
    
    // If no services loaded from markdown, use fallback
    if (services.length === 0) {
      console.log('No services loaded from markdown, using fallback');
      return getFallbackServices();
    }
  } catch (error) {
    console.error('Error loading services:', error);
    // Return fallback data
    return getFallbackServices();
  }
  
  console.log('Services loaded:', services.length);
  return services;
}

// Load all testimonials from markdown files
export async function loadTestimonials(): Promise<TestimonialData[]> {
  const testimonials: TestimonialData[] = [];
  
  try {
    // Import all testimonial markdown files
    const modules = import.meta.glob('/src/content/testimonials/*.md', { query: '?raw', import: 'default' });
    
    console.log('Loading testimonials, modules found:', Object.keys(modules).length);
    
    for (const path in modules) {
      const content = await modules[path]();
      if (typeof content === 'string') {
        const { data, content: markdownContent } = matter(content);
        
        // Extract slug from filename
        const filename = path.split('/').pop() || '';
        const slug = filename.replace('.md', '');
        
        // Get the first paragraph as the main content
        const firstParagraph = markdownContent.split('\n\n')[0] || markdownContent;
        
        testimonials.push({
          order: data.order || 0,
          name: data.name || 'Anonymous',
          role: data.role || '',
          company: data.company,
          rating: data.rating || 5,
          featured: data.featured || false,
          date: data.date,
          content: firstParagraph.trim(),
          slug
        });
      }
    }
    
    // Sort by order
    testimonials.sort((a, b) => a.order - b.order);
    
    // If no testimonials loaded from markdown, use fallback
    if (testimonials.length === 0) {
      console.log('No testimonials loaded from markdown, using fallback');
      return getFallbackTestimonials();
    }
  } catch (error) {
    console.error('Error loading testimonials:', error);
    // Return fallback data
    return getFallbackTestimonials();
  }
  
  console.log('Testimonials loaded:', testimonials.length);
  return testimonials;
}

// Fallback data in case markdown files can't be loaded
function getFallbackServices(): ServiceData[] {
  return [
    {
      order: 1,
      slug: '01-website-development',
      title: 'Modern Website Development',
      description: 'Fast, secure websites tailored to your needs — mobile-first, search-optimized, and future-ready. Choose a simple Static Presence, a professional business site, or an NGO-focused Community Hub.',
      icon: 'PcCase',
      iconAlternates: ['Computer', 'LaptopMinimal'],
      includes: [
        'Mobile-First Responsive Layout',
        'Content Management Setup',
        'SEO, Secure Hosting & Maintenance',
        { text: 'PWA App for Mobile & PC', hasTooltip: true, tooltipContent: 'PWA (Progressive Web App): A website that works like an app — installable on phone or computer, fast, and usable even offline.' }
      ]
    },
    {
      order: 2,
      slug: '02-cctv-streaming',
      title: 'CCTV Stream-to-Web',
      description: 'Powered by SentelBox — our proprietary hardware gateway — we connect your cameras and stream them securely with zero port forwarding or Static IP tax. Get password-protected embed links for your website, simple and hassle-free.',
      icon: 'Cctv',
      iconAlternates: ['ScanEye', 'Webcam'],
      includes: [
        'Powered by SentelBox (Our Proprietary Gateway)',
        'Secure Outbound Tunneling (no Static IP needed)',
        'Password-Protected Live Streams',
        'Cross-Platform Embed Links & Technical Support'
      ]
    },
    {
      order: 3,
      slug: '03-design-branding',
      title: 'Design & Branding',
      description: 'Strong visual identity sets you apart. We craft logos, brand systems, and digital design assets that connect with your audience.',
      icon: 'Palette',
      iconAlternates: ['Brush', 'PaintBucket'],
      includes: [
        'Logo & Brand Identity Design',
        'Visual Guidelines & Templates',
        'Marketing Collateral (social, print, digital)',
        'UI/UX Design for Apps & Websites'
      ]
    },
    {
      order: 4,
      slug: '04-product-packaging',
      title: 'Product & Packaging Design',
      description: 'Elevate your physical products with market-ready packaging design. We craft custom dielines, print-ready graphics, and unboxing experiences built to boost shelf appeal and sales.',
      icon: 'Package',
      iconAlternates: ['Box', 'Layers'],
      includes: [
        'Custom Dieline & Structural Layouts',
        'Print-Ready Artwork & Vector Files',
        'Material & Finish Specifications',
        'Label & Unboxing Design Systems'
      ]
    }
  ];
}

function getFallbackTestimonials(): TestimonialData[] {
  return [
    {
      order: 1,
      slug: 'getting-started',
      name: 'Paokhum Team',
      role: 'Web & CCTV Solutions',
      content: "We're just getting started. Real client stories will be added soon — maybe yours will be the first!",
      rating: 5
    }
  ];
}
